/**
 * Akan metnin React'e baglandigi TEK nokta.
 *
 * `streamStore` modul seviyesinde duz bir `Map` ve ag okuyucusu onu serbestce
 * mutate ediyor - React hic dahil degil. Bu hook `useSyncExternalStore` ile
 * TAM OLARAK bir bileseni (`<StreamingMessage>`) abone ediyor ve o da frame
 * basina EN FAZLA bir kez re-render ediyor.
 *
 * rAF dongusu de burada: yalnizca gosterilecek backlog varken kosuyor,
 * bitince kendini durduruyor. Surekli donen bir zamanlayici olmamasi onemli -
 * bos bir sohbet sayfasi CPU harcamiyor.
 */

import { useEffect, useMemo, useSyncExternalStore } from 'react'

import * as store from '../lib/streamStore'
import type { LiveTool } from '../lib/streamStore'
import { shouldSkipAnimation } from '../lib/typewriter'

export interface StreamView {
  /** Daktilonun o anki hali - render edilecek metin. */
  text: string
  /** Uretim bitti mi. */
  done: boolean
  /** Daktilo hala ilerliyor mu (caret gosterilmeli). */
  animating: boolean
  error: string | null
  model: string | null
  queuePosition: number | null
  lastSeq: number
  /** Yazilmakta/calismakta olan arac (bkz. `streamStore.LiveTool`). */
  live: LiveTool | null
}

export function useStreamingText(runId: string): StreamView {
  const version = useSyncExternalStore(
    (listener) => store.subscribe(runId, listener),
    () => store.getSnapshot(runId),
    // Sunucu tarafi render yok, ama `useSyncExternalStore` hidrasyonda bunu
    // istiyor; sabit 0 dogru cunku bos bir buffer'in surumu 0.
    () => 0,
  )

  // rAF dongusu: yalnizca backlog varken kosuyor.
  useEffect(() => {
    let frame = 0
    let stopped = false

    const pump = () => {
      if (stopped) return
      store.tick(runId)
      // `tick` -> `notify` -> `kick` zinciri burada bir daha zamanlama
      // YAPMIYOR cunku `frame` hala bu frame'in id'sini tutuyor.
      frame = store.isAnimating(runId) ? requestAnimationFrame(pump) : 0
    }

    const kick = () => {
      if (stopped || frame) return
      if (store.isAnimating(runId)) frame = requestAnimationFrame(pump)
    }

    const unsubscribe = store.subscribe(runId, kick)
    kick()

    return () => {
      stopped = true
      if (frame) cancelAnimationFrame(frame)
      unsubscribe()
    }
  }, [runId])

  // Gizli sekme ve `prefers-reduced-motion`: kalani ANINDA commit et.
  //
  // Tarayicilar arka planda rAF'i ~1 Hz'e kisiyor; bu olmadan daktilo
  // binlerce karakter geriye duser ve sekmeye donuldugunde gorunur bir
  // sarsintiyla "yetisir". CSS tek basina bir JS animasyonunu KAPATMIYOR -
  // reduced-motion tuzagi tam burada.
  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')

    const settle = () => {
      if (
        shouldSkipAnimation({
          reducedMotion: motion.matches,
          hidden: document.hidden,
          fromHistory: false,
          fromResume: false,
        })
      ) {
        store.commitAll(runId)
      }
    }

    settle()
    document.addEventListener('visibilitychange', settle)
    motion.addEventListener('change', settle)
    return () => {
      document.removeEventListener('visibilitychange', settle)
      motion.removeEventListener('change', settle)
    }
  }, [runId])

  // `version` degistikce yeniden hesapliyor: mutable buffer'dan okunan
  // degerler boylece render'a DEGISMEZ bir goruntu olarak giriyor.
  return useMemo<StreamView>(() => {
    const buffer = store.getBuffer(runId)
    return {
      text: store.visibleText(runId),
      done: buffer.netDone && !store.isAnimating(runId),
      animating: store.isAnimating(runId),
      error: buffer.error,
      model: buffer.model,
      queuePosition: buffer.queuePosition,
      lastSeq: buffer.lastSeq,
      live: buffer.live,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` kasitli
  }, [runId, version])
}
